import { extractDomainLabel, resolveDomainHeaderValue } from '@/config/flavors'

export const DOMAIN_HEADER_NAME = 'X-Domain-Name'
export const DEVICE_HEADER_NAME = 'X-Device-Name'

const DEVICE_NAME = 'browser'

// ---------------------------------------------------------------------------
// API Versioning — single source of truth
//
// The backend exposes multiple versioned prefixes. Keep defaults here so
// every service file references one place. Each can be overridden by the
// matching VITE_* env-var for per-deployment flexibility.
//
//   v2.0  –  Unified Online Booking Engine (auth, properties, bookings, payments)
//   bv1.0 –  Legacy property catalogue (deprecated)
//   v1.2  –  Legacy auth endpoints (deprecated)
// ---------------------------------------------------------------------------
export const API_VERSIONS = Object.freeze({
  /** V2 unified prefix for all online-booking endpoints */
  ONLINE_BOOKING: import.meta.env.VITE_ONLINE_BOOKING_API_VERSION || 'v2.0/online-booking',
  /** Properties — now served under the unified V2 prefix */
  PROPERTIES: import.meta.env.VITE_PROPERTIES_API_VERSION || 'v2.0/online-booking',
  /** Auth — now served under the unified V2 prefix */
  AUTH: import.meta.env.VITE_AUTH_API_VERSION || 'v2.0/online-booking',
})

/** Device identifier sent on all V2 auth requests (uppercase required). */
export const DEVICE_VALUE = 'web'

/** Default timeout for all API calls (ms). */
export const DEFAULT_API_TIMEOUT_MS = Number(import.meta.env.VITE_API_TIMEOUT_MS || 10000)

/** Base URL shared by most services unless a service-specific override exists. */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

/**
 * Detect whether the base URL already ends with /api.
 * Production has https://api.gopgms.com/api — dev has http://gopgmsapi.test (no /api).
 */
const baseEndsWithApi = (() => {
  const raw = String(API_BASE_URL).trim()
  if (!raw) return false
  try {
    const p = new URL(raw).pathname.replace(/\/+/g, '/').replace(/\/$/, '')
    return p === '/api' || p.endsWith('/api')
  } catch {
    const n = raw.replace(/\/+/g, '/').replace(/\/$/, '')
    return n === '/api' || n.endsWith('/api')
  }
})()

/**
 * Build a path that avoids double /api/ when the base URL already contains it.
 * Usage:  resolveApiPath('v2.0/online-booking')  →  '/api/v2.0/online-booking' or '/v2.0/online-booking'
 */
export const resolveApiPath = (tail) => {
  const cleaned = String(tail || '').replace(/^\/+/, '')
  return baseEndsWithApi ? `/${cleaned}` : `/api/${cleaned}`
}

const DEFAULT_APP_NAME = 'SANWARIAPGHOUSE'

/**
 * Derive app_name from the current domain.
 * e.g. bookabed.in → BOOKABED, sanwariapghouse.com → SANWARIAPGHOUSE
 * Falls back to SANWARIAPGHOUSE for localhost / unknown domains.
 */
export const resolveAppName = (hostname = (typeof window !== 'undefined' ? window.location.hostname : '')) => {
  const label = extractDomainLabel(hostname)
  const appName = label ? label.toUpperCase() : ''

  if (!appName || appName === 'LOCALHOST') {
    return DEFAULT_APP_NAME
  }

  return appName
}

export const getApiCommonHeaders = () => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
  const isLocal = hostname === 'localhost' || hostname === '127.0.0.1'

  // Always send the complete hostname as-is.
  // e.g. booking.gopgms.com → booking.gopgms.com
  //      sanwariapghouse.com → sanwariapghouse.com
  // For localhost / 127.0.0.1 fall back to the shared domain placeholder.
  const domainValue = isLocal ? 'booking.gopgms.com' : hostname

  const headers = {}
  headers[DOMAIN_HEADER_NAME] = domainValue
  headers[DEVICE_HEADER_NAME] = domainValue

  return headers
}

/**
 * Common payload fields to include in every API request body.
 */
export const getApiCommonPayload = () => ({
  device_name: DEVICE_NAME,
  app_name: resolveAppName()
})
